import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getDockerHosts,
  createDockerHost,
  deleteDockerHost,
  redeployHostAgent,
  getContainers,
  createContainer,
  containerAction,
  deleteContainer,
  getStacks,
  deployStack,
  stackAction,
  deleteStack,
  getDockerBillingSummary,
  getLiveContainers,
  liveContainerAction,
  deleteLiveContainer,
  getHostImages,
  pullHostImage,
  deleteHostImage,
  pruneHostImages,
  getHostVolumes,
  deleteHostVolume,
  pruneHostVolumes,
  getHostNetworks,
  pruneHostNetworks,
  getHostSystemDf,
} from "@/api/endpoints/docker";
import type {
  ContainerAction,
  StackAction,
  ContainerCreatePayload,
  DockerHostCreatePayload,
  StackCreatePayload,
} from "@/types/docker";

export function useDockerHosts() {
  return useQuery({
    queryKey: ["docker-hosts"],
    queryFn: getDockerHosts,
    // Poll while any host is still provisioning so the status updates live.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((h) => h.status === "provisioning")
        ? 4000
        : false,
  });
}

export function useContainers(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-containers", hostId],
    queryFn: () => getContainers(hostId as number),
    enabled: !!hostId,
  });
}

export function useDockerBilling() {
  return useQuery({
    queryKey: ["docker-billing"],
    queryFn: getDockerBillingSummary,
  });
}

// --- live explorer hooks ---------------------------------------------------

export function useLiveContainers(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-live-containers", hostId],
    queryFn: () => getLiveContainers(hostId as number),
    enabled: !!hostId,
    refetchInterval: 5000,
  });
}

export function useHostImages(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-images", hostId],
    queryFn: () => getHostImages(hostId as number),
    enabled: !!hostId,
  });
}

export function useHostVolumes(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-volumes", hostId],
    queryFn: () => getHostVolumes(hostId as number),
    enabled: !!hostId,
  });
}

export function useHostNetworks(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-networks", hostId],
    queryFn: () => getHostNetworks(hostId as number),
    enabled: !!hostId,
  });
}

export function useHostSystemDf(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-system-df", hostId],
    queryFn: () => getHostSystemDf(hostId as number),
    enabled: !!hostId,
  });
}

export function useLiveContainerAction(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cid, action }: { cid: string; action: ContainerAction }) =>
      liveContainerAction(hostId as number, cid, action),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["docker-live-containers", hostId],
      }),
  });
}

export function useDeleteLiveContainer(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cid: string) => deleteLiveContainer(hostId as number, cid),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["docker-live-containers", hostId],
      }),
  });
}

export function usePullImage(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ repository, tag }: { repository: string; tag?: string }) =>
      pullHostImage(hostId as number, repository, tag),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-images", hostId] }),
  });
}

export function useDeleteImage(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, force }: { imageId: string; force?: boolean }) =>
      deleteHostImage(hostId as number, imageId, force),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-images", hostId] }),
  });
}

export function usePruneImages(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => pruneHostImages(hostId as number),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-images", hostId] }),
  });
}

export function useDeleteVolume(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, force }: { name: string; force?: boolean }) =>
      deleteHostVolume(hostId as number, name, force),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-volumes", hostId] }),
  });
}

export function usePruneVolumes(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => pruneHostVolumes(hostId as number),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-volumes", hostId] }),
  });
}

export function usePruneNetworks(hostId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => pruneHostNetworks(hostId as number),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-networks", hostId] }),
  });
}

export function useStacks(hostId: number | undefined) {
  return useQuery({
    queryKey: ["docker-stacks", hostId],
    queryFn: () => getStacks(hostId as number),
    enabled: !!hostId,
  });
}

export function useCreateDockerHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DockerHostCreatePayload) => createDockerHost(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] }),
  });
}

export function useDeleteDockerHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hostId: number) => deleteDockerHost(hostId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] });
      queryClient.invalidateQueries({ queryKey: ["docker-billing"] });
    },
  });
}

export function useRedeployAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hostId: number) => redeployHostAgent(hostId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] }),
  });
}

export function useCreateContainer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      hostId,
      payload,
    }: {
      hostId: number;
      payload: ContainerCreatePayload;
    }) => createContainer(hostId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-containers"] }),
  });
}

export function useDeployStack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      hostId,
      payload,
    }: {
      hostId: number;
      payload: StackCreatePayload;
    }) => deployStack(hostId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-stacks"] }),
  });
}

export function useContainerAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: ContainerAction }) =>
      containerAction(id, action),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-containers"] }),
  });
}

export function useDeleteContainer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteContainer(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-containers"] }),
  });
}

export function useStackAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: StackAction }) =>
      stackAction(id, action),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-stacks"] }),
  });
}

export function useDeleteStack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, volumes }: { id: number; volumes?: boolean }) =>
      deleteStack(id, volumes),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["docker-stacks"] }),
  });
}
